import { computed, ref, type ComputedRef, type Ref } from 'vue'

/**
 * Snap-height + pointer-drag machinery for a bottom sheet, extracted from the
 * inline implementation in `TrackProfileToolbox.vue`. Modes are an ordered list
 * (lowest → highest); `heights` maps each mode to a pixel height. The sheet snaps
 * to the nearest mode on release, or jumps one step in the drag direction on a
 * fast flick.
 */
export interface UseBottomSheetOptions<Mode extends string> {
  /** Ordered list of snap modes, smallest height first. */
  modes: readonly Mode[]
  /** Reactive px height for each mode. */
  heights: ComputedRef<Record<Mode, number>> | Ref<Record<Mode, number>>
  /** Initial mode. Defaults to the first mode. */
  initialMode?: Mode
  /** Flick velocity (px/ms) above which a drag advances one mode. */
  flickVelocity?: number
  /**
   * When provided, the sheet can be swiped down past its smallest (peek) height to dismiss it:
   * released far enough below peek, or flicked down quickly while already at peek, calls this.
   * Without it the sheet clamps at peek (current behaviour) and is only closed by its own control.
   */
  onDismiss?: () => void
  /** Px below the peek height the sheet must be released to dismiss (default 72). */
  dismissThreshold?: number
}

export interface UseBottomSheet<Mode extends string> {
  mode: Ref<Mode>
  isDragging: Ref<boolean>
  /** Live height (drag height while dragging, else the current mode's height). */
  height: ComputedRef<number>
  /** Inline style for the sheet element (height incl. safe-area + drag transition). */
  sheetStyle: ComputedRef<Record<string, string>>
  onPointerDown: (event: PointerEvent) => void
  onPointerMove: (event: PointerEvent) => void
  onPointerUp: (event: PointerEvent) => void
  /** Programmatically snap to a mode (no animation suppression). */
  snapTo: (mode: Mode) => void
}

export function useBottomSheet<Mode extends string>(
  options: UseBottomSheetOptions<Mode>,
): UseBottomSheet<Mode> {
  const { modes, heights } = options
  const flickVelocity = options.flickVelocity ?? 0.35
  const onDismiss = options.onDismiss
  const dismissThreshold = options.dismissThreshold ?? 72

  const mode = ref(options.initialMode ?? modes[0]) as Ref<Mode>
  const isDragging = ref(false)
  const dragHeight = ref<number | null>(null)
  const startY = ref(0)
  const startHeight = ref(0)
  const startTime = ref(0)
  const pointerId = ref<number | null>(null)

  const minHeight = computed(() => Math.min(...modes.map((m) => heights.value[m])))
  const maxHeight = computed(() => Math.max(...modes.map((m) => heights.value[m])))

  const height = computed(() => dragHeight.value ?? heights.value[mode.value])

  const sheetStyle = computed<Record<string, string>>(() => ({
    height: `calc(${height.value}px + env(safe-area-inset-bottom))`,
    paddingBottom: 'env(safe-area-inset-bottom)',
    transition: isDragging.value ? 'none' : 'height 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  }))

  // When dismissable, allow dragging below the peek height so the sheet visibly "pulls off" the
  // bottom edge before release; otherwise it clamps at peek as before.
  const clampHeight = (value: number) => {
    const floor = onDismiss ? 0 : minHeight.value
    return Math.max(floor, Math.min(value, maxHeight.value))
  }

  const nearestMode = (value: number): Mode =>
    modes.reduce<Mode>((nearest, candidate) => {
      const nearestDistance = Math.abs(heights.value[nearest] - value)
      const candidateDistance = Math.abs(heights.value[candidate] - value)
      return candidateDistance < nearestDistance ? candidate : nearest
    }, modes[0])

  const onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return
    // Never start a drag from an interactive control inside the drag surface, so taps on
    // tabs / buttons / the rename input stay reliable.
    const origin = event.target as HTMLElement | null
    if (origin?.closest('button, a, input, select, textarea, [role="tab"]')) return
    event.preventDefault()
    pointerId.value = event.pointerId
    startY.value = event.clientY
    startHeight.value = height.value
    startTime.value = event.timeStamp
    dragHeight.value = height.value
    isDragging.value = true
    const target = event.currentTarget as HTMLElement | null
    target?.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: PointerEvent) => {
    if (!isDragging.value || pointerId.value !== event.pointerId) return
    event.preventDefault()
    const deltaY = event.clientY - startY.value
    dragHeight.value = clampHeight(startHeight.value - deltaY)
  }

  const onPointerUp = (event: PointerEvent) => {
    if (!isDragging.value || pointerId.value !== event.pointerId) return
    const deltaY = event.clientY - startY.value
    const deltaTime = Math.max(1, event.timeStamp - startTime.value)
    const velocity = Math.abs(deltaY) / deltaTime
    const currentIndex = modes.indexOf(mode.value)
    const releasedHeight = height.value

    const finishDrag = () => {
      dragHeight.value = null
      pointerId.value = null
      isDragging.value = false
      const target = event.currentTarget as HTMLElement | null
      if (target?.hasPointerCapture(event.pointerId)) {
        target.releasePointerCapture(event.pointerId)
      }
    }

    // Swipe-down-to-dismiss: pulled well below peek, or a quick downward flick while already at peek.
    if (onDismiss) {
      const draggedDown = deltaY > 0
      const pulledBelowPeek = releasedHeight < minHeight.value - dismissThreshold
      const flickedDownAtPeek = velocity > flickVelocity && draggedDown && currentIndex === 0
      if (pulledBelowPeek || flickedDownAtPeek) {
        finishDrag()
        onDismiss()
        return
      }
    }

    let next = nearestMode(releasedHeight)
    if (velocity > flickVelocity) {
      next =
        deltaY < 0
          ? modes[Math.min(modes.length - 1, currentIndex + 1)]
          : modes[Math.max(0, currentIndex - 1)]
    }

    mode.value = next
    finishDrag()
  }

  const snapTo = (next: Mode) => {
    if (isDragging.value) return
    mode.value = next
  }

  return {
    mode,
    isDragging,
    height,
    sheetStyle,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    snapTo,
  }
}
